import { rgbaToThumbHash, thumbHashToDataURL } from "thumbhash";

/**
 * Compact image placeholders, the way WhatsApp/Instagram do it: a tiny hash
 * captured at upload time and stored alongside the asset, decoded back into a
 * blurred preview that fills the (already reserved) media box before the real
 * pixels arrive — so there is never a grey flash and never a layout shift.
 *
 * We use ThumbHash (~25 bytes, base64 ~36 chars) rather than a fat data URL so
 * it stays cheap to carry inside chat message payloads.
 */

// ThumbHash requires the source raster to be ≤100px on its longest edge.
const MAX_THUMB_DIM = 100;

const bytesToBase64 = (bytes: Uint8Array): string => {
  let binary = "";
  for (let index = 0; index < bytes.length; index += 1) {
    binary += String.fromCharCode(bytes[index]);
  }
  return btoa(binary);
};

const base64ToBytes = (value: string): Uint8Array => {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
};

/**
 * Encode an already-loaded <img> into a ThumbHash string. Runs on the client
 * at send time, reusing the element we loaded for the natural-dimension probe.
 * Returns null whenever encoding is not possible (cross-origin taint, no 2d
 * context, zero-size image) — callers treat the hash as optional.
 */
export const encodeThumbHashFromImage = (
  image: HTMLImageElement,
): string | null => {
  const naturalWidth = image.naturalWidth;
  const naturalHeight = image.naturalHeight;
  if (!naturalWidth || !naturalHeight) {
    return null;
  }

  try {
    const scale = Math.min(
      1,
      MAX_THUMB_DIM / Math.max(naturalWidth, naturalHeight),
    );
    const width = Math.max(1, Math.round(naturalWidth * scale));
    const height = Math.max(1, Math.round(naturalHeight * scale));

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d", { willReadFrequently: true });
    if (!context) {
      return null;
    }

    context.drawImage(image, 0, 0, width, height);
    const { data } = context.getImageData(0, 0, width, height);
    return bytesToBase64(rgbaToThumbHash(width, height, data));
  } catch {
    return null;
  }
};

// Decoding rebuilds a small PNG; memoize per hash so a chat list re-render does
// not redo the work for every visible bubble.
const dataUrlCache = new Map<string, string>();
const DATA_URL_CACHE_LIMIT = 200;

/**
 * Decode a stored ThumbHash string back into a PNG data URL suitable for
 * `next/image`'s `blurDataURL`. Returns undefined for missing/invalid hashes.
 */
export const blurHashToDataUrl = (
  value: string | null | undefined,
): string | undefined => {
  if (!value) {
    return undefined;
  }

  const cached = dataUrlCache.get(value);
  if (cached) {
    return cached;
  }

  try {
    const dataUrl = thumbHashToDataURL(base64ToBytes(value));
    if (dataUrlCache.size >= DATA_URL_CACHE_LIMIT) {
      dataUrlCache.clear();
    }
    dataUrlCache.set(value, dataUrl);
    return dataUrl;
  } catch {
    return undefined;
  }
};
